import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import ErrorMessage from '../components/shared/ErrorMessage';
import SkeletonCard from '../components/shared/SkeletonCard';
import projectsApi, { type Project } from '../services/projectsApi';
import './ProjectsListing.css';

const CATEGORIES = ['All', 'research', 'development', 'design', 'data-science', 'ai-ml'];

type TabType = 'missions' | 'research';

const ProjectsListing: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('research');
  const [projects, setProjects] = useState<Project[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState('Tümü');

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Mission veya Research projelerini yükle (SADECE is_featured = 1)
      const projectType = activeTab === 'missions' ? 'mission' : 'research';
      const data = await projectsApi.getProjects(undefined, projectType);
      const projectsArray = (data.projects || []) as Project[];

      // SADECE featured projeleri goster
      const featuredProjects = projectsArray.filter(p => p.is_featured === 1);
      setProjects(featuredProjects);
      setFilteredProjects(featuredProjects);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    if (activeCategory === 'All') {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(
        projects.filter(p => p.category === activeCategory)
      );
    }
  }, [activeCategory, projects]);

  const handleProjectClick = (projectId: number) => {
    navigate(`/projects/${projectId}`);
  };

  const handleRetry = () => {
    void loadProjects();
  };

  return (
    <MainLayout>
      <div className="projects-listing-page">
        <div className="projects-listing-container">
        <div className="projects-listing-header">
          <h1>All Projects</h1>
          <p>{filteredProjects.length} active projects</p>
        </div>

        {/* Tabs: Missions vs Research */}
        <div style={{
          display: 'flex',
          gap: '10px',
          marginBottom: '20px',
          borderBottom: '2px solid var(--border-color)'
        }}>
          <button
            onClick={() => setActiveTab('research')}
            style={{
              padding: '12px 24px',
              background: activeTab === 'research' ? 'var(--accent-blue)' : 'transparent',
              color: activeTab === 'research' ? 'white' : 'var(--text-secondary)',
              border: 'none',
              borderBottom: activeTab === 'research' ? '3px solid var(--accent-blue)' : 'none',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '16px',
              transition: 'all 0.2s'
            }}
          >
            Research Projects
          </button>
          <button
            onClick={() => setActiveTab('missions')}
            style={{
              padding: '12px 24px',
              background: activeTab === 'missions' ? 'var(--accent-purple)' : 'transparent',
              color: activeTab === 'missions' ? 'white' : 'var(--text-secondary)',
              border: 'none',
              borderBottom: activeTab === 'missions' ? '3px solid var(--accent-purple)' : 'none',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '16px',
              transition: 'all 0.2s'
            }}
          >
            Mission Projects
          </button>
        </div>

        <div className="filters">
          {CATEGORIES.map(category => (
            <button
              key={category}
              className={`filter-btn ${activeCategory === category ? 'active' : ''}`}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {error && (
          <ErrorMessage message={error} onRetry={handleRetry} />
        )}

        {loading ? (
          <div className="projects-grid">
            {[...Array(6)].map((_, i) => (
              <SkeletonCard key={i} variant="project" />
            ))}
          </div>
        ) : (
          <div className="projects-grid">
            {filteredProjects.map(project => (
              <div
                key={project.id}
                className="project-card"
                onClick={() => handleProjectClick(project.id)}
              >
                <div className="project-header">
                  <span className="project-emoji">{activeTab === 'missions' ? '🎯' : '🔬'}</span>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span className="project-badge">{activeTab === 'missions' ? 'Mission' : 'Research'}</span>
                    {project.visibility === 'restricted' && (
                      <span style={{
                        padding: '4px 8px',
                        background: '#f59e0b',
                        color: 'white',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600'
                      }}>
                        🔒 Outputs Private
                      </span>
                    )}
                    {project.visibility === 'private' && (
                      <span style={{
                        padding: '4px 8px',
                        background: '#ef4444',
                        color: 'white',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600'
                      }}>
                        🔒 Private
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="project-title">{project.title}</div>
                  <div className="project-desc">{project.description}</div>
                </div>
                <div className="project-stats">
                  <div className="project-stat">
                    <div className="project-stat-value">{project.members_count || 0}</div>
                    <div className="project-stat-label">Members</div>
                  </div>
                  <div className="project-stat">
                    <div className="project-stat-value">{project.agents_count || 0}</div>
                    <div className="project-stat-label">Agents</div>
                  </div>
                  <div className="project-stat">
                    <div className="project-stat-value">
                      {activeTab === 'research' ? (project.findings_count || 0) : (project.tasks_count || 0)}
                    </div>
                    <div className="project-stat-label">
                      {activeTab === 'research' ? 'Findings' : 'Tasks'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && !error && filteredProjects.length === 0 && (
          <div className="empty-state">
            <p>No projects found in this category.</p>
          </div>
        )}
        </div>
      </div>
    </MainLayout>
  );
};

export default ProjectsListing;
